import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { Database } from "@/integrations/supabase/types";

export type Property = Database["public"]["Tables"]["properties"]["Row"];

export type CohostProperty = Property & {
  access_level: "full" | "limited";
};

type CohostRow = {
  access_level: string;
  properties: Property | null;
};

/**
 * Properties where the current user is an accepted co-host (property_cohosts).
 */
export const useCohostProperties = () => {
  const { user } = useAuth();

  return useQuery<CohostProperty[]>({
    queryKey: ["cohost-properties", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await (
        supabase as unknown as {
          from: (table: string) => ReturnType<typeof supabase.from>;
        }
      )
        .from("property_cohosts")
        .select("access_level, properties:properties(*)")
        .eq("cohost_user_id", user.id)
        .not("accepted_at", "is", null);

      if (error) {
        console.error("[useCohostProperties] Supabase error:", error);
        throw error;
      }

      const rows = (data ?? []) as unknown as CohostRow[];
      const result: CohostProperty[] = [];

      for (const row of rows) {
        const property = row.properties;
        if (!property) continue;
        if (property.archived_at != null || property.deleted_at != null) continue;

        const level = row.access_level === "full" ? "full" : "limited";
        result.push({ ...property, access_level: level });
      }

      if (process.env.NODE_ENV === "development") {
        console.log(`[useCohostProperties] User ${user.id} sees ${result.length} co-host properties`);
      }

      return result;
    },
    enabled: !!user?.id,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 10_000,
  });
};
